"use client";

import CloseRounded from "@mui/icons-material/CloseRounded";
import FilterAltRounded from "@mui/icons-material/FilterAltRounded";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useState, type ReactNode } from "react";

import { radius } from "../tokens/radius";

export type MobileFiltersDrawerProps = {
  /** Nombre de filtres actifs affiché dans le badge du déclencheur. */
  activeCount: number;
  /** Libellé du bouton déclencheur et titre du panneau. */
  label: string;
  /** Aria-label du bouton de fermeture. */
  closeLabel: string;
  /** Libellé du bouton de validation (ex. « Voir les 12 résultats »). */
  applyLabel: string;
  /** Produit `${prefix}-open` et `${prefix}-apply`. */
  testIdPrefix: string;
  /** Contenu des filtres (la sidebar réutilisée telle quelle). */
  children: ReactNode;
};

/**
 * Déclencheur « Filtres » + bottom sheet mobile.
 *
 * Les filtres s'appliquent en direct — le contenu pilote son propre état — et
 * le bouton de validation se contente de refermer le panneau pour révéler les
 * résultats. Il n'y a donc rien à annuler : fermer sans valider ne remet rien
 * en arrière, et c'est voulu.
 */
export function MobileFiltersDrawer({
  activeCount,
  label,
  closeLabel,
  applyLabel,
  testIdPrefix,
  children,
}: MobileFiltersDrawerProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Badge badgeContent={activeCount} color="primary">
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterAltRounded fontSize="small" />}
          onClick={() => setOpen(true)}
          data-testid={`${testIdPrefix}-open`}
        >
          {label}
        </Button>
      </Badge>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              // Valeurs en string : dans `sx`, un rayon numérique est
              // multiplié par `shape.borderRadius` (8), donc `16` donnait
              // 128 px au lieu des 16 px voulus.
              borderTopLeftRadius: radius.xl,
              borderTopRightRadius: radius.xl,
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h4" component="h2">
            {label}
          </Typography>
          <IconButton onClick={() => setOpen(false)} aria-label={closeLabel}>
            <CloseRounded />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>{children}</Box>

        <Stack
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            p: 2,
          }}
        >
          <Button
            variant="contained"
            fullWidth
            onClick={() => setOpen(false)}
            data-testid={`${testIdPrefix}-apply`}
          >
            {applyLabel}
          </Button>
        </Stack>
      </Drawer>
    </>
  );
}
